import { Item, select } from "gamedata/GameData";
import { ReactNode, useState } from "react";
import { Link } from "react-router";
import { z } from "zod";
import { ControlBox } from "~/components/form/ControlBox";
import { MultiSelect } from "~/components/form/MultiSelect";
import { ItemLink } from "~/components/item/ItemLink";
import { EntityTooltip } from "~/components/site/Tooltip";
import { Breadcrumb } from "~/components/ui/Breadcrumb";
import { Button } from "~/components/ui/Button";
import { Icon, Icon32 } from "~/components/ui/Icon";
import { PageTitle } from "~/components/ui/PageTitle";
import { Table } from "~/components/ui/Table";
import { GD_ALL } from "~/gd";
import { FISH_TYPE, FISH_TYPE_NAMES, FishType } from "~/types";
import { gameName } from "~/util/common";
import { createFiltering } from "~/util/Filtering";
import { lstring, tt, useRealm, useT, useTurl } from "~/util/Lang";
import { applyPaging, Pager } from "~/util/Listing";
import { $LOADER, $PAGE, MetaArgs, OurLoaderArgs } from "~/util/Loaders";
import { logPageView } from "~/util/stats";
import { homePage } from "~/util/titles";
import {
  urlQueryFromSearchParams,
  useSetUrlQuery,
  useUrlQuery,
} from "~/util/UrlUtils";

type Config = {
  filter?: (item: Item) => boolean;
};

const PageQuery = z.object({
  p: z.optional(z.string()),
  s: z.optional(z.string()),
});

type ItemFilter = {
  type: readonly FishType[];
};

const FILTER = createFiltering<ItemFilter, Item>({
  pack: (f) => ({
    type: f.type?.join(","),
  }),
  unpack: (r) => {
    const type = FishType.array().parse(r.type ? r.type.split(",") : []);
    return {
      type,
    };
  },
  filter: (item, f) => {
    if (f.type.length > 0 && !item.itemName.includes(f.type as any)) {
      return false;
    }

    return true;
  },
});

async function makeData(la: OurLoaderArgs, cfg: Config) {
  // Log
  logPageView(la);
  //
  const t = tt.bind(null, la.realm.lang);
  const query = PageQuery.parse(
    Object.fromEntries(la.url.searchParams.entries())
  );
  const filter = FILTER.fromUrlQuery(
    urlQueryFromSearchParams(la.url.searchParams)
  );

  const all = Array.from(GD_ALL[la.realm.game].items.values()).filter(
    (i) =>
      (i.itemName.includes("_treasure_box") &&
        !i.itemName.includes("key_of_treasure")) ||
      (i.itemName.includes("_fish") &&
        !i.itemName.includes("_fish_") &&
        !i.itemName.includes("_fishing") &&
        !i.itemName.includes("q_box_of_fish"))
  );
  const filtered = FILTER.apply(all, filter, cfg.filter);
  const [paged, paging] = applyPaging(filtered, query);

  const items = select(paged, {
    select: {
      id: true,
      itemName: true,
      icon: true,
      name: t,
      contains: {
        select: {
          items: {
            select: {
              item: {
                id: true,
                itemName: true,
                icon: true,
                name: t,
              },
              count: true,
            },
          },
          chance: true,
        },
      },
    },
  });

  return { items, paging, authUser: la.req.sess?.user };
}

type LoaderData = Awaited<ReturnType<typeof makeData>>;

function makeMeta(
  cx: MetaArgs<LoaderData>,
  opts: { title: lstring; canonicalPath?: string }
) {
  const { data } = cx;
  let title = cx.t(opts.title);
  if (data.paging.page > 1) {
    title += ` (${cx.t({ en: "Page", ru: "Страница" })} ${data.paging.page})`;
  }
  return {
    title: `${title} - ${homePage(cx.realm)}`,
    description: cx.t({
      en: `A complete list of Fish ${gameName(
        cx.realm.game
      )} with its contents and convenient filtering`,
      ru: `Полный список Рыбы ${gameName(
        cx.realm.game
      )} с её содержимым и удобной фильтрацией`,
    }),
    ogTitle: `Lineage 2 Fish`,
  };
}

$PAGE({
  path: "items/fish",
  loader: $LOADER?.((la) => {
    return makeData(la, {});
  }),
  meta: (cx) =>
    makeMeta(cx, {
      title: { en: "Fish", ru: "Рыба" },
      canonicalPath: "/items/fish",
    }),
  render: ({ data }) => {
    const t = useT();
    const turl = useTurl();
    const fishTitle = t({ en: "Fish", ru: "Рыба" });
    const pager = <Pager paging={data.paging} />;

    return (
      <div>
        <div className="content">
          <Breadcrumb items={[{ title: fishTitle, url: turl("/fish") }]} />
          <PageTitle>{fishTitle}</PageTitle>
          <Filters />
        </div>
        {pager}
        <div style={{ overflowX: "auto" }}>
          <Table
            data={data.items}
            id={(item, i) => i}
            cols={[
              {
                title: "",
                cell: (item) => (
                  <EntityTooltip what={{ t: "item", uname: item.itemName }}>
                    {({ setReference, getReferenceProps }) => (
                      <div
                        ref={setReference}
                        {...getReferenceProps({
                          style: {
                            display: "flex",
                          },
                        })}
                      >
                        <Icon32 icon={item.icon} />
                      </div>
                    )}
                  </EntityTooltip>
                ),
                thPadding: "0",
                padding: "8px 0 8px 8px",
                width: 32 + 12,
              },
              {
                title: t({ en: "Name", ru: "Название" }),
                cell: (item) => (
                  <>
                    <div className="text-white font-medium">
                      <Link to={turl(`/items/${item.itemName}`)}>
                        {item.name}
                      </Link>
                    </div>
                  </>
                ),
              },
              {
                title: t({ en: "Contains", ru: "Содержит" }),
                cell: (item) => (
                  <div>
                    {item.contains.map((c, i) => (
                      <div
                        key={i}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        {c.items.map((item, j) => (
                          <div key={j}>
                            <ItemLink item={item.item} count={item.count} />
                          </div>
                        ))}
                        <div>{c.chance}%</div>
                      </div>
                    ))}
                  </div>
                ),
              },
            ]}
          />
        </div>
        {pager}
      </div>
    );
  },
});

function Filters(props: { pages?: ReactNode }) {
  const realm = useRealm();
  const t = useT();
  const urlQuery = useUrlQuery();
  const setUrlQuery = useSetUrlQuery();

  const [filter, setFilter] = useState(() => FILTER.fromUrlQuery(urlQuery));

  return (
    <div style={{ padding: "0 5px" }}>
      <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
        <ControlBox
          title="Type"
          reset={() => setFilter((f) => ({ ...f, type: [] }))}
        >
          <MultiSelect
            options={FISH_TYPE.map((type) => ({
              value: type,
              title: t(FISH_TYPE_NAMES[type]),
            }))}
            value={filter.type}
            onChange={(type) =>
              setFilter((f) => ({ ...f, type: FishType.array().parse(type) }))
            }
          />
        </ControlBox>
      </div>
      <div
        style={{
          alignItems: "flex-end",
          display: "flex",
          justifyContent: "space-between",
          paddingTop: 20,
        }}
      >
        <div style={{ padding: "0 0 8px" }}>
          <Button
            onClick={() => {
              setUrlQuery(
                { ...urlQuery, f: FILTER.encode(filter) },
                { preventScrollReset: true }
              );
            }}
          >
            Apply
          </Button>
        </div>
        {props.pages}
      </div>
    </div>
  );
}
